<template>
  <div class="modern-chat-wrapper">
    <!-- Header moderne -->
    <div class="chat-header">
      <div class="user-info">
        <div class="avatar-container">
          <img src="@/assets/pp.png" alt="avatar" class="user-avatar" />
          <div class="status-indicator online"></div>
        </div>
        <div class="user-details">
          <h3 class="user-name">{{ clientName }}</h3>
          <span class="user-status">Online</span>
        </div>
      </div>
    </div>

    <!-- Zone de chat avec scroll automatique -->
    <div class="chat-messages" ref="chatMessages">
      <div class="messages-container">
        <div v-for="(msg, index) in messages" :key="`${msg.id || msg.timestamp}-${index}`" class="message-wrapper">
          <div
            :class="[
              'message-bubble',
              msg.sender === managerUsername ? 'sent' : 'received',
              'animate-in',
              (msg.products && msg.products.length) ? 'has-products' : ''
            ]"
          >
            <!-- Text message -->
            <div v-if="msg.type === 'text' || !msg.type" class="message-content">{{ msg.content }}</div>

            <!-- Image message - CORRIGÉ -->
        <div v-else-if="msg.type === 'image'" class="media-message">
          <div class="image-container" :class="{ 'error': imageErrors[msg.file_url] }">
            <img
              v-if="!imageErrors[msg.file_url]"
              :src="msg.file_url"
              alt="Image"
              class="message-image"
              @click="openImageModal(msg.file_url)"
              @error="handleImageError(msg.file_url)"
            />
            <div v-else class="image-error">
              <i class="fas fa-image"></i>
              <span>Image non disponible</span>
            </div>
            <div v-if="msg.content" class="media-caption">{{ msg.content }}</div>
          </div>
        </div>
            <!-- Video message - CORRIGÉ -->
            <div v-else-if="msg.type === 'video'" class="media-message">
              <div class="video-container" :class="{ 'error': videoErrors[msg.file_url] }">
                <!-- Erreur vidéo -->
                <div v-if="videoErrors[msg.file_url]" class="video-error">
                  <i class="fas fa-video"></i>
                  <span>Vidéo non disponible</span>
                </div>

                <!-- Vidéo -->
                <video
                  v-else
                  :src="msg.file_url"
                  class="message-video"
                  controls
                  preload="metadata"
                  playsinline
                  webkit-playsinline
                  @error="onVideoError(msg.file_url)"
                  @loadeddata="onVideoLoad(msg.file_url)"
                >
                  Votre navigateur ne supporte pas la lecture vidéo.
                </video>
              </div>
              <div v-if="msg.content" class="media-caption">{{ msg.content }}</div>
            </div>

            <!-- File message -->
            <div v-else-if="msg.type === 'file'" class="media-message">
              <div class="file-container">
                <div class="file-icon">
                  <i :class="getFileIcon(msg.file_name)"></i>
                </div>
                <div class="file-info">
                  <span class="file-name">{{ msg.file_name || 'File' }}</span>
                  <span class="file-size">{{ formatFileSize(msg.file_size) }}</span>
                </div>
                <a :href="msg.file_url" download :download="msg.file_name" class="file-download-btn">
                  <i class="fas fa-download"></i>
                </a>
              </div>
              <div v-if="msg.content" class="media-caption">{{ msg.content }}</div>
            </div>

            <!-- Products attached by the manager -->
            <div v-if="msg.products && msg.products.length" class="msg-products">
              <div v-if="msg.products.length > 1" class="msg-products-head">
                <i class="fas fa-box-open"></i>
                <span>{{ msg.products.length }} products from the marketplace</span>
              </div>
              <div
                v-for="prod in msg.products"
                :key="'prod-' + (prod.id || prod.name)"
                class="msg-product-share"
                @click="goToRecommended(msg, prod)"
              >
                <div class="mps-media">
                  <img v-if="prod.image" :src="prod.image" :alt="prod.name" loading="lazy" />
                  <span v-else class="mps-media-ph">{{ (prod.category || prod.name || 'P').charAt(0) }}</span>
                </div>
                <div class="mps-info">
                  <div class="mps-name">{{ truncateProduct(prod.name, 46) }}</div>
                  <div class="mps-price">{{ formatProductPrice(prod.price) }}</div>
                  <div class="mps-foot">
                    <span v-if="prod.category" class="mps-cat"><i class="fas fa-tag"></i> {{ prod.category }}</span>
                    <span class="mps-cta">See product <i class="fas fa-chevron-right"></i></span>
                  </div>
                </div>
              </div>
              <button class="msg-all-products-btn" @click="goToRecommended(msg, null)">
                <i class="fas fa-th-large"></i> View all products
              </button>
            </div>

            <div class="message-meta">
              <span class="message-time">{{ formatTime(msg.timestamp) }}</span>
              <i v-if="msg.sender === managerUsername" class="fas fa-check message-status"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Unread messages badge -->
      <transition name="bounce">
        <div v-if="unreadCount > 0" class="unread-badge" @click="scrollToBottom(true)">
          <i class="fas fa-arrow-down"></i>
          <span>{{ unreadCount }}</span>
        </div>
      </transition>
    </div>

    <!-- File preview -->
    <div v-if="selectedFile" class="file-preview-container">
      <div class="file-preview">
        <!-- Image preview -->
        <div v-if="filePreview.type === 'image'" class="preview-content">
          <div class="preview-image-container">
            <img :src="filePreview.url" alt="Preview" class="preview-image" @error="onPreviewError" />
          </div>
        </div>

        <!-- Video preview -->
        <div v-else-if="filePreview.type === 'video'" class="preview-content">
          <div class="preview-video-container">
            <video :src="filePreview.url" class="preview-video" controls muted @error="onPreviewError"></video>
          </div>
        </div>

        <!-- File preview -->
        <div v-else class="preview-content file-preview-content">
          <div class="file-preview-icon">
            <i :class="getFileIcon(selectedFile.name)"></i>
          </div>
          <div class="file-preview-info">
            <span class="file-preview-name">{{ selectedFile.name }}</span>
            <span class="file-preview-size">{{ formatFileSize(selectedFile.size) }}</span>
          </div>
        </div>

        <button @click="removeFile" class="remove-preview-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Caption input area -->
      <div class="caption-input-container">
        <input
          v-model="fileCaption"
          placeholder="Add a caption (optional)..."
          class="caption-input"
        />
      </div>
    </div>

    <!-- Modern input -->
    <div class="chat-input-container">
      <div class="input-wrapper">
        <!-- Attachment buttons -->
        <div class="attachment-section">
          <div class="attachment-dropdown" :class="{ active: showAttachmentMenu }">
            <button
              class="attachment-btn"
              @click="showAttachmentMenu = !showAttachmentMenu"
              :class="{ active: showAttachmentMenu }"
            >
              <i class="fas fa-paperclip"></i>
            </button>

            <transition name="slide-up">
              <div v-if="showAttachmentMenu" class="attachment-menu">
                <button @click="selectFile('image')" class="attachment-option">
                  <i class="fas fa-image"></i>
                  <span>Photo</span>
                </button>
                <button @click="selectFile('video')" class="attachment-option">
                  <i class="fas fa-video"></i>
                  <span>Video</span>
                </button>
                <button @click="selectFile('file')" class="attachment-option">
                  <i class="fas fa-file"></i>
                  <span>Document</span>
                </button>
              </div>
            </transition>
          </div>
        </div>

        <!-- Emoji section -->
        <div class="emoji-section">
          <button
            class="emoji-btn"
            @click="showEmojiPicker = !showEmojiPicker"
            :class="{ active: showEmojiPicker }"
          >
            <i class="fas fa-smile"></i>
          </button>

          <transition name="slide-up">
            <div v-if="showEmojiPicker" class="emoji-picker-overlay" ref="emojiPicker">
              <Picker
                :data="data"
                set="apple"
                title="Pick your emoji"
                emoji="point_up"
                @select="addEmoji"
              />
            </div>
          </transition>
        </div>

        <!-- Text input section -->
        <div class="text-input-section">
          <textarea
            ref="messageInput"
            v-model="newMessage"
            placeholder="Type your message..."
            @keydown.enter.exact.prevent="sendMessage"
            @input="adjustTextareaHeight"
            class="message-input"
            rows="1"
            :disabled="selectedFile && !fileCaption && !newMessage"
          ></textarea>
        </div>

        <!-- Send button -->
        <transition name="scale">
          <button
            v-if="newMessage.trim() !== '' || selectedFile"
            @click="sendMessage"
            class="send-btn"
            :disabled="sending"
          >
            <i v-if="!sending" class="fas fa-paper-plane"></i>
            <i v-else class="fas fa-spinner fa-spin"></i>
          </button>
        </transition>
      </div>
    </div>

    <!-- Hidden file inputs -->
    <input
      ref="imageInput"
      type="file"
      accept="image/*"
      style="display: none"
      @change="handleFileSelect"
    />
    <input
      ref="videoInput"
      type="file"
      accept="video/*"
      style="display: none"
      @change="handleFileSelect"
    />
    <input
      ref="fileInput"
      type="file"
      accept="*/*"
      style="display: none"
      @change="handleFileSelect"
    />

    <!-- Image Modal -->
    <div v-if="showImageModal" class="image-modal-overlay" @click="closeImageModal">
      <div class="image-modal" @click.stop>
        <img :src="selectedImageUrl" alt="Full size image" @error="onModalImageError" />
        <button @click="closeImageModal" class="close-modal-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Picker } from 'emoji-mart-vue';
import 'emoji-mart-vue/css/emoji-mart.css';

export default {
  components: {
    Picker,
  },
  data() {
    return {
      messages: [],
      clientName: '',
      managerUsername: '',
      newMessage: '',
      showEmojiPicker: false,
      unreadCount: 0,
      autoScrollEnabled: true,
      sending: false,
      lastMessageCount: 0,
      pollingInterval: null,

      // File handling
      selectedFile: null,
      fileCaption: '',
      filePreview: { type: null, url: null },
      showAttachmentMenu: false,

      // Image modal
      showImageModal: false,
      selectedImageUrl: '',

      // Message deduplication
      messageMap: new Map(),

      // Media loading states
      imageLoaded: {},
      imageErrors: {},
      videoErrors: {},
      previewError: false,

      // ✨ NOUVEAU: SSE
      eventSource: null,
      reconnectAttempts: 0,
      maxReconnectAttempts: 5,
    };
  },

  mounted() {
    this.fetchMessages();

    // ✨ REMPLACER setupPolling par initSSE
    this.initSSE();

    this.setupEventListeners();

    // ✨ NOUVEAU: Gérer la visibilité de la page
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        if (!this.eventSource || this.eventSource.readyState === EventSource.CLOSED) {
          console.log('🔄 Page visible, reconnecting main chat SSE...')
          this.initSSE()
        }
      }
    })
  },

  beforeDestroy() {
    this.cleanup();
    this.closeSSE();
  },

  methods: {
    async fetchMessages() {
      try {
        const response = await axios.post('/api/v1/home/get_chat_history/');
        const newMessages = response.data.list_message || [];

        this.processMessages(newMessages);
        this.clientName = response.data.nom;
        this.managerUsername = response.data.priority;

        const wasNewMessage = this.messages.length > this.lastMessageCount;
        this.lastMessageCount = this.messages.length;

        if (wasNewMessage && !this.autoScrollEnabled) {
          this.unreadCount++;
        }

        this.$nextTick(() => {
          if (this.autoScrollEnabled || this.messages.length <= 1) {
            this.scrollToBottom();
          }
        });

      } catch (error) {
        console.error('Error loading chat:', error);
      }
    },

    processMessages(newMessages) {
      const tempMap = new Map();
      const processedMessages = [];

      newMessages.forEach(msg => {
        const uniqueKey = this.createMessageKey(msg);
        const dedupeKey = this.createDeduplicationKey(msg);
        const isDuplicate = processedMessages.some(existingMsg =>
          this.createDeduplicationKey(existingMsg) === dedupeKey
        );

        if (!tempMap.has(uniqueKey) && !isDuplicate) {
          tempMap.set(uniqueKey, msg);
          processedMessages.push(msg);
        }

        if (msg.type === 'image') {
          if (!this.imageLoaded[msg.file_url]) {
            this.$set(this.imageLoaded, msg.file_url, false);
            this.$set(this.imageErrors, msg.file_url, false);
          }
        }
        if (msg.type === 'video') {
          if (!this.videoErrors[msg.file_url]) {
            this.$set(this.videoErrors, msg.file_url, false);
          }
        }
      });

      processedMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      this.messageMap = tempMap;
      this.messages = processedMessages;
      this.$nextTick(() => this.refreshTranslation());
    },

    createDeduplicationKey(msg) {
      const timestamp = msg.timestamp ? new Date(msg.timestamp).getTime() : Date.now();
      const normalizedTimestamp = Math.floor(timestamp / 1000) * 1000;

      const sender = msg.sender || 'unknown';
      const content = (msg.content || '').trim();
      const type = msg.type || 'text';
      const fileUrl = msg.file_url || '';

      return `dedupe-${normalizedTimestamp}-${sender}-${type}-${content}-${fileUrl}`;
    },

    createMessageKey(msg) {
      if (msg.id) return `id-${msg.id}`;

      const timestamp = msg.timestamp ? new Date(msg.timestamp).getTime() : Date.now();
      const sender = msg.sender || 'unknown';
      const content = msg.content || '';
      const type = msg.type || 'text';
      const fileUrl = msg.file_url || '';

      return `fallback-${timestamp}-${sender}-${type}-${content.substring(0, 10)}-${fileUrl.substring(0, 20)}`;
    },

    refreshTranslation() {
      if (this.$translator && typeof this.$translator.refreshIfEnabled === 'function') {
        this.$translator.refreshIfEnabled('dom', this.$el);
      }
    },

    async sendMessage() {
      if ((!this.newMessage.trim() && !this.selectedFile) || this.sending) return;

      this.sending = true;

      const messageToSend = this.newMessage.trim();
      const fileToSend = this.selectedFile;
      const captionToSend = this.fileCaption.trim();

      // Réinitialiser le formulaire AVANT l'envoi
      this.resetForm();

      try {
        const formData = new FormData();

        if (fileToSend) {
          formData.append('file', fileToSend);
          formData.append('message_type', this.getFileType(fileToSend));

          if (captionToSend) {
            formData.append('message', captionToSend);
          } else if (messageToSend) {
            formData.append('message', messageToSend);
          }
        } else {
          formData.append('message', messageToSend);
          formData.append('message_type', 'text');
        }

        await axios.post('/api/v1/home/save_chat/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // Attendre un peu avant de récupérer les nouveaux messages
        setTimeout(() => {
          this.fetchMessages();
        }, 500);

      } catch (error) {
        console.error('Error sending message:', error);
        alert('Failed to send message. Please try again.');
      } finally {
        this.sending = false;
      }
    },

    resetForm() {
      this.newMessage = '';
      this.fileCaption = '';
      this.selectedFile = null;
      this.filePreview = { type: null, url: null };
      this.previewError = false;
      this.showAttachmentMenu = false;
      this.showEmojiPicker = false;

      this.$nextTick(() => {
        if (this.$refs.messageInput) {
          this.$refs.messageInput.value = '';
          this.adjustTextareaHeight();
          this.$refs.messageInput.blur();
          setTimeout(() => {
            if (this.$refs.messageInput) {
              this.$refs.messageInput.focus();
            }
          }, 100);
        }
      });
    },

    // ✨ NOUVELLE MÉTHODE: Initialiser SSE
    initSSE() {
      const token = this.$store.state.token || localStorage.getItem('token')

      if (!token) {
        console.error('❌ No auth token available for main chat SSE')
        this.setupPolling()
        return
      }

      if (this.eventSource) {
        this.eventSource.close()
      }

      const url = `http://localhost:8000/api/v1/home/chat_stream/?token=${token}`
      this.eventSource = new EventSource(url)

      this.eventSource.onopen = () => {
        console.log('✅ Main Chat SSE Connection established')
        this.reconnectAttempts = 0
      }

      this.eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)

          if (data.type === 'connected') {
            console.log('📡 Main Chat SSE Stream connected')
            return
          }

          if (data.type === 'new_message') {
            console.log('📨 New message in main chat:', data)

            // Recharger les messages immédiatement
            this.fetchMessages()
          }
        } catch (error) {
          console.error('❌ Error parsing main chat SSE message:', error)
        }
      }

      this.eventSource.onerror = (error) => {
        console.error('❌ Main Chat SSE error:', error)
        this.eventSource.close()
        this.attemptReconnect()
      }
    },

    // ✨ NOUVELLE MÉTHODE: Reconnexion automatique
    attemptReconnect() {
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++
        const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000)

        console.log(`🔄 Main Chat reconnecting SSE (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms...`)

        setTimeout(() => {
          this.initSSE()
        }, delay)
      } else {
        console.error('⚠️ Main chat max reconnection attempts. Using fallback polling.')
        this.setupPolling()
      }
    },

    // ✨ MÉTHODE MODIFIÉE: Polling de secours (10 secondes au lieu de 3)
    setupPolling() {
      console.log('⚠️ Main chat using fallback polling (every 10 seconds)')

      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
      }

      this.pollingInterval = setInterval(() => {
        this.fetchMessages()
      }, 1000000)
    },

    // ✨ NOUVELLE MÉTHODE: Fermer SSE
    closeSSE() {
      if (this.eventSource) {
        this.eventSource.close()
        this.eventSource = null
        console.log('🔌 Main Chat SSE closed')
      }
    },

    selectFile(type) {
      this.showAttachmentMenu = false;

      if (type === 'image') {
        this.$refs.imageInput.click();
      } else if (type === 'video') {
        this.$refs.videoInput.click();
      } else {
        this.$refs.fileInput.click();
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      const maxSize = file.type.startsWith('video/') ? 2000 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        const maxSizeMB = maxSize / (1024 * 1024);
        alert(`File size must be less than ${maxSizeMB}MB`);
        return;
      }

      this.selectedFile = file;
      this.previewError = false;
      this.createFilePreview(file);

      event.target.value = '';
    },

    createFilePreview(file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        this.filePreview.url = e.target.result;

        if (file.type.startsWith('image/')) {
          this.filePreview.type = 'image';
        } else if (file.type.startsWith('video/')) {
          this.filePreview.type = 'video';
        } else {
          this.filePreview.type = 'file';
        }
      };

      reader.onerror = () => {
        this.previewError = true;
        console.error('Error reading file for preview');
      };

      reader.readAsDataURL(file);
    },

    removeFile() {
      this.selectedFile = null;
      this.fileCaption = '';
      this.filePreview = { type: null, url: null };
      this.previewError = false;
    },

    getFileType(file) {
      if (file.type.startsWith('image/')) return 'image';
      if (file.type.startsWith('video/')) return 'video';
      return 'file';
    },

    getFileIcon(fileName) {
      if (!fileName) return 'fas fa-file';

      const ext = fileName.split('.').pop().toLowerCase();

      const iconMap = {
        pdf: 'fas fa-file-pdf',
        doc: 'fas fa-file-word',
        docx: 'fas fa-file-word',
        xls: 'fas fa-file-excel',
        xlsx: 'fas fa-file-excel',
        ppt: 'fas fa-file-powerpoint',
        pptx: 'fas fa-file-powerpoint',
        txt: 'fas fa-file-alt',
        jpg: 'fas fa-file-image',
        jpeg: 'fas fa-file-image',
        png: 'fas fa-file-image',
        gif: 'fas fa-file-image',
        svg: 'fas fa-file-image',
        mp4: 'fas fa-file-video',
        avi: 'fas fa-file-video',
        mov: 'fas fa-file-video',
        wmv: 'fas fa-file-video',
        zip: 'fas fa-file-archive',
        rar: 'fas fa-file-archive',
        '7z': 'fas fa-file-archive',
      };

      return iconMap[ext] || 'fas fa-file';
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 B';

      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    },

    goToRecommended(msg, prod) {
      const route = prod && prod.id
        ? `/market_place/recommended/${msg.id}?highlight=${prod.id}`
        : `/market_place/recommended/${msg.id}`;
      this.$router.push(route);
    },

    truncateProduct(text, max) {
      if (!text) return '';
      return text.length > max ? text.substring(0, max) + '…' : text;
    },

    formatProductPrice(value) {
      if (value === null || value === undefined || value === '') return '';
      const num = Number(value);
      if (isNaN(num)) return String(value);
      return num.toLocaleString('fr-FR') + ' FCFA';
    },

    onImageLoad(imageUrl) {
      this.$set(this.imageLoaded, imageUrl, true);
      if (this.imageErrors[imageUrl]) {
        this.$delete(this.imageErrors, imageUrl);
      }
    },

    onImageError(imageUrl) {
      console.error('Failed to load image:', imageUrl);
      this.$set(this.imageErrors, imageUrl, true);
      this.$set(this.imageLoaded, imageUrl, false);
    },

    onVideoLoad(videoUrl) {
      if (this.videoErrors[videoUrl]) {
        this.$delete(this.videoErrors, videoUrl);
      }
    },

    onVideoError(videoUrl) {
      console.error('Failed to load video:', videoUrl);
      this.$set(this.videoErrors, videoUrl, true);
    },

    onPreviewError() {
      this.previewError = true;
      console.error('Error loading preview');
    },

    onModalImageError() {
      console.error('Error loading modal image');
    },

    openImageModal(imageUrl) {
      if (this.imageLoaded[imageUrl] && !this.imageErrors[imageUrl]) {
        this.selectedImageUrl = imageUrl;
        this.showImageModal = true;
      }
    },

    handleImageError(imageUrl) {
      console.error('Image load error:', imageUrl);
      this.$set(this.imageErrors, imageUrl, true);

      setTimeout(() => {
        if (this.imageErrors[imageUrl]) {
          this.$delete(this.imageErrors, imageUrl);
        }
      }, 2000);
    },

    closeImageModal() {
      this.showImageModal = false;
      this.selectedImageUrl = '';
    },

    setupEventListeners() {
      document.addEventListener('click', this.handleOutsideClick);
      if (this.$refs.chatMessages) {
        this.$refs.chatMessages.addEventListener('scroll', this.checkScrollPosition);
      }
    },

    cleanup() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
      }
      document.removeEventListener('click', this.handleOutsideClick);
      if (this.$refs.chatMessages) {
        this.$refs.chatMessages.removeEventListener('scroll', this.checkScrollPosition);
      }
    },

    scrollToBottom(force = false) {
      const container = this.$refs.chatMessages;
      if (container) {
        container.scrollTop = container.scrollHeight;
        if (force) {
          this.unreadCount = 0;
          this.autoScrollEnabled = true;
        }
      }
    },

    checkScrollPosition() {
      const container = this.$refs.chatMessages;
      const threshold = 100;
      this.autoScrollEnabled =
        container.scrollTop + container.clientHeight >= container.scrollHeight - threshold;
    },

    addEmoji(emoji) {
      this.newMessage += emoji.native;
      this.showEmojiPicker = false;
      this.$nextTick(() => {
        this.$refs.messageInput.focus();
        this.adjustTextareaHeight();
      });
    },

    adjustTextareaHeight() {
      const textarea = this.$refs.messageInput;
      if (textarea) {
        textarea.style.height = 'auto';
        const newHeight = Math.min(textarea.scrollHeight, 120);
        textarea.style.height = newHeight + 'px';
      }
    },

    handleOutsideClick(event) {
      const emojiPicker = this.$refs.emojiPicker;
      if (emojiPicker && !emojiPicker.contains(event.target) &&
          !event.target.closest('.emoji-btn')) {
        this.showEmojiPicker = false;
      }

      if (!event.target.closest('.attachment-section')) {
        this.showAttachmentMenu = false;
      }
    },

    formatTime(timestamp) {
      const date = new Date(timestamp);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

      if (msgDate.getTime() === today.getTime()) {
        return date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        });
      } else {
        return date.toLocaleDateString('en-US', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    },
  },
};
</script>

<style scoped>
.modern-chat-wrapper {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 140px);
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0,0,0,0.1);
}

.chat-header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255,255,255,0.2);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar-container {
  position: relative;
}

.user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #1976d2;
}

.status-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid white;
}

.status-indicator.online {
  background: #4CAF50;
}

.user-name {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
}

.user-status {
  color: #7f8c8d;
  font-size: 13px;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  position: relative;
}

.messages-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.message-bubble {
  max-width: 75%;
  padding: 12px 16px;
  border-radius: 18px;
  position: relative;
  word-wrap: break-word;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
}

.message-bubble:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
}

.sent {
  align-self: flex-end;
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  border-bottom-right-radius: 6px;
}

.received {
  align-self: flex-start;
  background: white;
  color: #2c3e50;
  border-bottom-left-radius: 6px;
}

.message-content {
  font-size: 15px;
  line-height: 1.4;
  margin-bottom: 4px;
}

/* Product cards sent by the manager */
.msg-products {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.msg-products-head {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: #00796b;
}

.msg-products-head i { font-size: 12px; }

/* WhatsApp-style product share card */
.msg-product-share {
  display: flex;
  align-items: stretch;
  gap: 10px;
  background: #ffffff;
  border: 1px solid #eceff1;
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.msg-product-share:hover {
  transform: translateY(-2px);
  border-color: #00897b;
  box-shadow: 0 6px 18px rgba(0, 137, 123, 0.22);
}

.mps-media {
  width: 84px;
  min-width: 84px;
  height: auto;
  min-height: 84px;
  background: linear-gradient(135deg, rgba(0, 150, 136, 0.08), rgba(0, 100, 80, 0.14));
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 137, 123, 0.35);
  font-size: 30px;
}

.mps-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mps-media-ph {
  font-size: 30px;
  font-weight: 900;
  color: #00897b;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.mps-info {
  flex: 1;
  min-width: 0;
  padding: 9px 10px 9px 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.mps-name {
  font-size: 13px;
  font-weight: 800;
  color: #263238;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.mps-price {
  font-size: 14px;
  font-weight: 900;
  color: #00897b;
}

.mps-foot {
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.mps-cat {
  font-size: 10.5px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.45);
  text-transform: capitalize;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.mps-cat i { font-size: 9px; }

.mps-cta {
  font-size: 11px;
  font-weight: 800;
  color: #00796b;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.mps-cta i { font-size: 10px; transition: transform 0.2s ease; }
.msg-product-share:hover .mps-cta i { transform: translateX(3px); }

.msg-all-products-btn {
  width: 100%;
  padding: 8px;
  background: rgba(0, 137, 123, 0.1);
  border: 1px dashed rgba(0, 137, 123, 0.45);
  border-radius: 12px;
  color: #00796b;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: all 0.2s ease;
}

.msg-all-products-btn:hover {
  background: rgba(0, 137, 123, 0.18);
}

/* Sent-bubble overrides (gradient background) */
.message-bubble.sent .msg-products-head {
  color: rgba(255, 255, 255, 0.92);
}

.message-bubble.sent .msg-all-products-btn {
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: #fff;
  backdrop-filter: blur(3px);
}

.message-bubble.sent .msg-all-products-btn:hover {
  background: rgba(255, 255, 255, 0.26);
}

.message-bubble.sent .msg-product-share { background: #fff; }

/* Wider bubbles when the manager attaches products */
.message-bubble.has-products {
  max-width: 88%;
  padding: 10px 12px 12px;
}

@media (max-width: 768px) {
  .message-bubble.has-products { max-width: 93%; }
  .mps-media { width: 68px; min-width: 68px; min-height: 68px; font-size: 24px; }
}

/* STYLES AMÉLIORÉS pour les médias */
.media-message {
  max-width: 100%;
}

.image-container {
  position: relative;
  margin-bottom: 8px;
  border-radius: 12px;
  overflow: hidden;
  background: #f5f5f5;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-container {
  position: relative;
  margin-bottom: 8px;
  border-radius: 12px;
  overflow: hidden;
  background: #000;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Loader pour images */
.image-loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #1976d2;
  font-size: 24px;
  z-index: 2;
}

/* États d'erreur pour images et vidéos */
.image-error, .video-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
  gap: 8px;
  padding: 20px;
  text-align: center;
}

.image-error i, .video-error i {
  font-size: 32px;
  color: #ccc;
}

.image-container.error, .video-container.error {
  background: #f8f9fa;
  border: 2px dashed #e0e0e0;
}

/* Styles des images et vidéos */
.message-image {
  width: 100%;
  max-width: 280px;
  height: auto;
  min-height: 150px;
  max-height: 300px;
  object-fit: cover;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 12px;
  display: block;
}

.message-image:hover {
  transform: scale(1.02);
  filter: brightness(0.9);
}

.message-video {
  width: 100%;
  max-width: 300px;
  max-height: 200px;
  border-radius: 12px;
  object-fit: cover;
  background: #000;
  outline: none;
}

/* Conteneurs spéciaux pour messages envoyés/reçus */
.sent .image-container, .sent .video-container {
  background: rgba(255,255,255,0.1);
}

.sent .message-image {
  border: 1px solid rgba(255,255,255,0.2);
}

.received .image-container {
  background: #f8f9fa;
}

.received .video-container {
  background: #f8f9fa;
}

.received .message-image, .received .message-video {
  border: 1px solid rgba(0,0,0,0.05);
}

/* File containers */
.file-container {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: rgba(255,255,255,0.1);
  border-radius: 10px;
  min-width: 200px;
}

.received .file-container {
  background: #f5f5f5;
}

.file-icon {
  font-size: 24px;
  color: #1976d2;
  width: 32px;
  text-align: center;
}

.file-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.file-name {
  font-weight: 500;
  font-size: 14px;
  word-break: break-all;
}

.file-size {
  font-size: 12px;
  opacity: 0.7;
}

.file-download-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255,255,255,0.2);
  color: inherit;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.received .file-download-btn {
  background: #e0e0e0;
  color: #666;
}

.file-download-btn:hover {
  background: rgba(255,255,255,0.3);
  transform: scale(1.1);
}

.received .file-download-btn:hover {
  background: #d0d0d0;
}

.media-caption {
  margin-top: 8px;
  font-size: 14px;
  opacity: 0.9;
  padding: 0 4px;
}

.message-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  opacity: 0.7;
  font-size: 11px;
  margin-top: 4px;
}

.sent .message-meta {
  color: rgba(255,255,255,0.8);
}

.received .message-meta {
  color: #95a5a6;
}

.unread-badge {
  position: fixed;
  bottom: 120px;
  right: 30px;
  background: #1976d2;
  color: white;
  border-radius: 25px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 20px rgba(25, 118, 210, 0.4);
  z-index: 100;
}

/* STYLES AMÉLIORÉS pour la prévisualisation */
.file-preview-container {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(255,255,255,0.2);
  padding: 16px 20px;
}

.file-preview {
  position: relative;
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  margin-bottom: 12px;
}

.preview-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.preview-image-container, .preview-video-container {
  border-radius: 8px;
  overflow: hidden;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.preview-image {
  max-width: 120px;
  max-height: 80px;
  border-radius: 8px;
  object-fit: cover;
  display: block;
}

.preview-video {
  max-width: 120px;
  max-height: 80px;
  border-radius: 8px;
  object-fit: cover;
}

.file-preview-content {
  align-items: center;
}

.file-preview-icon {
  font-size: 32px;
  color: #1976d2;
  width: 48px;
  text-align: center;
}

.file-preview-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.file-preview-name {
  font-weight: 500;
  font-size: 14px;
  color: #2c3e50;
  word-break: break-all;
}

.file-preview-size {
  font-size: 12px;
  color: #7f8c8d;
}

.remove-preview-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border: none;
  background: rgba(0,0,0,0.6);
  color: white;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.remove-preview-btn:hover {
  background: rgba(0,0,0,0.8);
  transform: scale(1.1);
}

.caption-input-container {
  margin-top: 8px;
}

.caption-input {
  width: 100%;
  border: none;
  outline: none;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f5f5f5;
  font-size: 14px;
  color: #2c3e50;
}

.caption-input::placeholder {
  color: #bdc3c7;
}

.chat-input-container {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 16px 20px;
  border-top: 1px solid rgba(255,255,255,0.2);
}

.input-wrapper {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  background: white;
  border-radius: 25px;
  padding: 8px;
  box-shadow: 0 2px 20px rgba(0,0,0,0.1);
}

/* Attachment section */
.attachment-section {
  position: relative;
}

.attachment-dropdown {
  position: relative;
}

.attachment-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  color: #95a5a6;
}

.attachment-btn:hover,
.attachment-btn.active {
  background: #e3f2fd;
  color: #1976d2;
  transform: scale(1.1);
}

.attachment-menu {
  position: absolute;
  bottom: 50px;
  left: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.2);
  overflow: hidden;
  z-index: 1000;
  min-width: 120px;
}

.attachment-option {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: white;
  color: #2c3e50;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}

.attachment-option:hover {
  background: #f8f9fa;
}

.attachment-option i {
  width: 20px;
  color: #1976d2;
}

.emoji-section {
  position: relative;
}

.emoji-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  color: #95a5a6;
}

.emoji-btn:hover,
.emoji-btn.active {
  background: #e3f2fd;
  color: #1976d2;
  transform: scale(1.1);
}

.emoji-picker-overlay {
  position: absolute;
  bottom: 50px;
  left: 0;
  z-index: 1000;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0,0,0,0.2);
}

.text-input-section {
  flex: 1;
}

.message-input {
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  font-size: 15px;
  line-height: 1.4;
  padding: 8px 12px;
  background: transparent;
  color: #2c3e50;
  font-family: inherit;
}

.message-input::placeholder {
  color: #bdc3c7;
}

.message-input:disabled {
  opacity: 0.6;
}

.send-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(25, 118, 210, 0.4);
}

.send-btn:hover {
  transform: scale(1.1) rotate(5deg);
}

.send-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

/* Image Modal */
.image-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.image-modal {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  animation: zoomIn 0.3s ease;
}

@keyframes zoomIn {
  from { transform: scale(0.8); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.image-modal img {
  max-width: 100%;
  max-height: 100%;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.5);
}

.close-modal-btn {
  position: absolute;
  top: -15px;
  right: -15px;
  width: 40px;
  height: 40px;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
  transition: all 0.3s ease;
}

.close-modal-btn:hover {
  background: white;
  transform: scale(1.1);
}

/* Animations */
.animate-in {
  animation: slideInUp 0.4s ease-out;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.bounce-enter-active {
  animation: bounce-in 0.5s;
}

.bounce-leave-active {
  animation: bounce-in 0.5s reverse;
}

@keyframes bounce-in {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.slide-up-enter-active, .slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter, .slide-up-leave-to {
  transform: translateY(10px);
  opacity: 0;
}

.scale-enter-active, .scale-leave-active {
  transition: all 0.2s ease;
}

.scale-enter, .scale-leave-to {
  transform: scale(0);
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .modern-chat-wrapper {
    height: calc(100vh - 100px);
    border-radius: 0;
  }

  .message-bubble {
    max-width: 85%;
    font-size: 14px;
  }

  .chat-messages {
    padding: 15px;
  }

  .user-avatar {
    width: 40px;
    height: 40px;
  }

  .message-image {
    max-width: 220px;
    max-height: 250px;
  }

  .message-video {
    max-width: 220px;
  }

  .input-wrapper {
    gap: 6px;
    padding: 6px;
  }

  .attachment-btn, .emoji-btn {
    width: 32px;
    height: 32px;
  }

  .send-btn {
    width: 36px;
    height: 36px;
  }

  .file-preview {
    padding: 12px;
  }

  .preview-image, .preview-video {
    max-width: 80px;
    max-height: 60px;
  }
}

/* Loading states pour les médias */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.fa-spinner {
  animation: spin 1s linear infinite;
}

/* États de chargement spécifiques */
.image-container.loading {
  background: #f8f9fa;
  border: 2px dashed #e0e0e0;
}

.video-container.loading {
  background: #f0f0f0;
  border: 2px dashed #e0e0e0;
}

/* Amélioration de la gestion des erreurs */
.image-container.error .image-error,
.video-container.error .video-error {
  width: 100%;
  height: 100%;
  min-height: 150px;
}

/* Styles pour les contrôles vidéo personnalisés */
.message-video::-webkit-media-controls-panel {
  background-color: rgba(0, 0, 0, 0.8);
}

.message-video::-webkit-media-controls-play-button,
.message-video::-webkit-media-controls-volume-slider {
  background-color: #1976d2;
}

/* Amélioration de l'accessibilité */
.message-image[role="button"],
.preview-image[role="button"] {
  cursor: pointer;
  outline: 2px solid transparent;
  transition: outline 0.3s ease;
}

.message-image[role="button"]:focus,
.preview-image[role="button"]:focus {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}
</style>